// URL-Zustand der Suchseite: `/search?q=…&server=…&channel=…&sort=…&scope=…` (Teilen/Zurück funktionieren).
import type { SearchSort } from '../api';

export type ScopeMode = 'all' | 'server' | 'channel';
export const SORTS: readonly SearchSort[] = ['newest', 'oldest', 'relevance'];

export interface SearchUrlState {
  q: string;
  /** Kontext-Server (aus dem Einstieg), unabhängig vom gewählten Bereich. */
  server: string | null;
  /** Kontext-Kanal. */
  channel: string | null;
  sort: SearchSort;
  scope: ScopeMode;
}

const clean = (v: string | null | undefined): string | null => (v && v !== '@me' ? v : null);

/** Standard-Bereich: so eng wie der Kontext (Kanal > Server > überall). */
export function defaultScope(server: string | null, channel: string | null): ScopeMode {
  return channel ? 'channel' : server ? 'server' : 'all';
}

export function readSearchUrl(sp: URLSearchParams): SearchUrlState {
  const server = clean(sp.get('server'));
  const channel = clean(sp.get('channel'));
  const sortRaw = sp.get('sort');
  const sort = SORTS.includes(sortRaw as SearchSort) ? (sortRaw as SearchSort) : 'newest';
  const scopeRaw = sp.get('scope');
  let scope: ScopeMode = scopeRaw === 'all' || scopeRaw === 'server' || scopeRaw === 'channel' ? scopeRaw : defaultScope(server, channel);
  if (scope === 'channel' && !channel) scope = defaultScope(server, channel);
  if (scope === 'server' && !server) scope = defaultScope(null, channel) === 'channel' ? 'channel' : 'all';
  return { q: sp.get('q') ?? '', server, channel, sort, scope };
}

/** Query-Parameter in stabiler Reihenfolge; Standardwerte (sort=newest, Standard-Bereich) entfallen. */
export function searchQuery(state: Partial<SearchUrlState>): URLSearchParams {
  const sp = new URLSearchParams();
  const server = clean(state.server);
  const channel = clean(state.channel);
  if (state.q) sp.set('q', state.q);
  if (server) sp.set('server', server);
  if (channel) sp.set('channel', channel);
  if (state.sort && state.sort !== 'newest') sp.set('sort', state.sort);
  if (state.scope && state.scope !== defaultScope(server, channel)) sp.set('scope', state.scope);
  return sp;
}

export function searchUrl(state: Partial<SearchUrlState>): string {
  const s = searchQuery(state).toString();
  return s ? `/search?${s}` : '/search';
}

/** IDs für die API aus dem gewählten Bereich. */
export function scopeIds(state: SearchUrlState): { serverId?: string; channelId?: string } {
  if (state.scope === 'channel' && state.channel) return { serverId: state.server ?? undefined, channelId: state.channel };
  if (state.scope === 'server' && state.server) return { serverId: state.server };
  return {};
}
